/**
 * MfaSetupModal
 *
 * Multi-step modal for the TOTP MFA setup flow:
 *   Step 1 — QR code: Call /mfa/setup, display QR (or manual secret), store backup codes
 *   Step 2 — Verify: Enter 6-digit TOTP code, call /mfa/verify
 *   Step 3 — Recovery codes: Show backup codes; require "I have saved" checkbox before dismiss
 *
 * Epic: MVP 1.15 (V2) — Federated Auth, Epic 4: MFA
 * Story: SCRUM-356 — Guide MFA Setup
 */

import React, { useState, useEffect } from 'react';
import { X, Loader, Smartphone, ShieldCheck, AlertCircle } from 'lucide-react';
import { mfaApi } from '../../services/mfaApi';
import RecoveryCodesDisplay from './RecoveryCodesDisplay';

const STEPS = {
  QR: 1,
  VERIFY: 2,
  RECOVERY: 3,
};

/**
 * @param {Object}   props
 * @param {boolean}  props.open        - Whether the modal is open
 * @param {Function} props.onClose     - Called when modal is dismissed
 * @param {Function} props.onSuccess   - Called after codes are acknowledged (MFA is active)
 */
export default function MfaSetupModal({ open, onClose, onSuccess }) {
  const [step, setStep] = useState(STEPS.QR);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Data from setup call
  const [secret, setSecret] = useState(null);
  const [qrCodeSrc, setQrCodeSrc] = useState(null);
  const [backupCodes, setBackupCodes] = useState([]);

  // Verify step inputs
  const [totpCode, setTotpCode] = useState('');

  // Recovery step
  const [savedConfirmed, setSavedConfirmed] = useState(false);

  // Initiate setup when modal opens
  useEffect(() => {
    if (!open) { return; }
    setStep(STEPS.QR);
    setError(null);
    setTotpCode('');
    setSavedConfirmed(false);
    initiateSetup();
  }, [open]);

  const initiateSetup = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await mfaApi.initiateSetup();
      const data = response?.data || {};
      setSecret(data.secret || null);
      // Handle both field names (controller maps as qrCode, service returns qrCodeUrl)
      setQrCodeSrc(data.qrCode || data.qrCodeUrl || null);
      setBackupCodes(Array.isArray(data.backupCodes) ? data.backupCodes : []);
    } catch (err) {
      setError(err?.message || 'Failed to initiate MFA setup. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async (e) => {
    e.preventDefault();
    if (!totpCode || totpCode.length !== 6) {
      setError('Please enter a 6-digit code from your authenticator app.');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      await mfaApi.verifySetup(totpCode);
      setStep(STEPS.RECOVERY);
    } catch (err) {
      setError(err?.message || 'Invalid code. Please check your authenticator app and try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleDone = () => {
    onSuccess();
    onClose();
  };

  if (!open) { return null; }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50" onClick={step !== STEPS.RECOVERY ? onClose : undefined} />

      {/* Modal */}
      <div className="relative bg-white rounded-xl shadow-xl w-full max-w-md mx-4 p-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-5">
          <h2 className="text-lg font-semibold text-gray-900">
            {step === STEPS.QR && 'Set Up Authenticator App'}
            {step === STEPS.VERIFY && 'Verify Your Authenticator'}
            {step === STEPS.RECOVERY && 'Save Your Recovery Codes'}
          </h2>
          {step !== STEPS.RECOVERY && (
            <button
              type="button"
              onClick={onClose}
              className="p-1 rounded hover:bg-gray-100 transition-colors"
              aria-label="Close"
            >
              <X size={18} className="text-gray-500" />
            </button>
          )}
        </div>

        {/* Step indicator */}
        <div className="flex gap-1.5 mb-5">
          {[1, 2, 3].map((s) => (
            <div
              key={s}
              className={`h-1.5 flex-1 rounded-full transition-colors ${
                s <= step ? 'bg-brand-primary' : 'bg-gray-200'
              }`}
            />
          ))}
        </div>

        {/* Error */}
        {error && (
          <div className="flex items-start gap-2 p-3 mb-4 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
            <AlertCircle size={16} className="mt-0.5 shrink-0" />
            <span>{error}</span>
          </div>
        )}

        {/* ── Step 1: QR Code ── */}
        {step === STEPS.QR && (
          <div className="space-y-4">
            {loading ? (
              <div className="flex flex-col items-center py-8 gap-3">
                <Loader size={24} className="animate-spin text-brand-primary" />
                <p className="text-sm text-gray-500">Generating setup code…</p>
              </div>
            ) : (
              <>
                <p className="text-sm text-gray-600">
                  Scan the QR code below with your authenticator app (Google Authenticator, Authy, etc.).
                </p>

                {qrCodeSrc ? (
                  <div className="flex justify-center">
                    <img
                      src={qrCodeSrc}
                      alt="MFA QR Code"
                      className="w-48 h-48 border border-gray-200 rounded-lg"
                    />
                  </div>
                ) : (
                  <div className="flex items-start gap-2 p-3 bg-yellow-50 border border-yellow-200 rounded-lg text-sm text-yellow-700">
                    <Smartphone size={16} className="mt-0.5 shrink-0" />
                    <span>QR code unavailable. Enter the secret key manually in your authenticator app.</span>
                  </div>
                )}

                {secret && (
                  <div>
                    <p className="text-xs text-gray-500 mb-1">Manual entry key:</p>
                    <code className="block px-3 py-2 bg-gray-50 border border-gray-200 rounded text-sm font-mono text-gray-800 break-all select-all">
                      {secret}
                    </code>
                  </div>
                )}

                {!error && (
                  <button
                    type="button"
                    onClick={() => setStep(STEPS.VERIFY)}
                    className="w-full py-2.5 bg-brand-primary text-white rounded-lg font-medium hover:bg-brand-primary/90 transition-colors"
                  >
                    I&apos;ve scanned the code — Continue
                  </button>
                )}
              </>
            )}
          </div>
        )}

        {/* ── Step 2: Verify TOTP ── */}
        {step === STEPS.VERIFY && (
          <form onSubmit={handleVerify} className="space-y-4">
            <p className="text-sm text-gray-600">
              Enter the 6-digit code shown in your authenticator app to verify setup.
            </p>

            <div>
              <label htmlFor="totp-code" className="block text-sm font-medium text-gray-700 mb-1">
                Verification Code
              </label>
              <input
                id="totp-code"
                type="text"
                inputMode="numeric"
                maxLength={6}
                pattern="\d{6}"
                value={totpCode}
                onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder="000000"
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-center text-2xl font-mono tracking-[0.5em] focus:outline-none focus:ring-2 focus:ring-brand-primary focus:border-brand-primary"
                autoComplete="one-time-code"
                autoFocus
              />
            </div>

            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => setStep(STEPS.QR)}
                className="flex-1 py-2.5 border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50 transition-colors"
              >
                Back
              </button>
              <button
                type="submit"
                disabled={loading || totpCode.length !== 6}
                className="flex-1 py-2.5 bg-brand-primary text-white rounded-lg font-medium hover:bg-brand-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
              >
                {loading ? <Loader size={16} className="animate-spin" /> : null}
                Verify & Enable
              </button>
            </div>
          </form>
        )}

        {/* ── Step 3: Recovery Codes ── */}
        {step === STEPS.RECOVERY && (
          <div className="space-y-4">
            <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-700">
              <ShieldCheck size={16} className="mt-0.5 shrink-0" />
              <span>
                <strong>MFA is now active.</strong> Save these recovery codes in a secure location.
                They are shown only once and cannot be recovered if lost.
              </span>
            </div>

            {backupCodes.length > 0 ? (
              <RecoveryCodesDisplay codes={backupCodes} />
            ) : (
              <p className="text-sm text-gray-500 italic">No recovery codes returned by server.</p>
            )}

            <label className="flex items-start gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={savedConfirmed}
                onChange={(e) => setSavedConfirmed(e.target.checked)}
                className="mt-0.5 h-4 w-4 rounded border-gray-300 text-brand-primary focus:ring-brand-primary"
              />
              <span className="text-sm text-gray-700">
                I have saved these recovery codes in a secure location.
              </span>
            </label>

            <button
              type="button"
              onClick={handleDone}
              disabled={!savedConfirmed}
              className="w-full py-2.5 bg-brand-primary text-white rounded-lg font-medium hover:bg-brand-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              Done
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
